import { useState, useEffect } from 'react';
import { Stats, ChartData, ListItem } from '../types/dashboard.types';
import imagePaths from '../../conf/imageConfig';
import dataProvider from '../../services/api';


export const useDashboardData = () => {
  const [stats, setStats] = useState<Stats>({
    monthlyIncome: '0.00',
    newOrders: 0,
    activeOrders: 0,
    newUsers: 0,
  });
  const [incomeData, setIncomeData] = useState<ChartData[]>([]);
  const [ordersData, setOrdersData] = useState<ChartData[]>([]);
  const [pendingItems, setPendingItems] = useState<ListItem[]>([]);
  const [activeOrders, setActiveOrders] = useState<ListItem[]>([]);
  const [recentTopups, setRecentTopups] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();
        const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();

        // Fetch stats
        const [incomeResponse, newOrdersResponse, activeOrdersResponse, newUsersResponse] = await Promise.all([
          dataProvider.getTotalMonthlyIncome(),
          dataProvider.getMonthlyNewOrders(),
          dataProvider.getMonthlyActiveOrders(),
          dataProvider.getMonthlyNewUsers(),
        ]);

        setStats({
          monthlyIncome: incomeResponse.data.incomeCount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          newOrders: newOrdersResponse.data.orderCount,
          activeOrders: activeOrdersResponse.data.orderCount,
          newUsers: newUsersResponse.data.accountCount,
        });

        // Prepare chart data
        const tempIncomeData: ChartData[] = Array.from({ length: daysInMonth }, (_, i) => ({
          day: i + 1,
          income: 0,
        }));
        const tempOrdersData: ChartData[] = Array.from({ length: daysInMonth }, (_, i) => ({
          day: i + 1,
          orders: 0,
        }));

        // Fetch daily data (this part might need optimization)
        const currentDay = currentDate.getDate();
        for (let day = 1; day <= currentDay; day++) {
          const [dailyIncomeResponse, dailyOrdersResponse] = await Promise.all([
            dataProvider.getDailyIncome(),
            dataProvider.getDailyNewOrders(),
          ]);
          tempIncomeData[day - 1].income = dailyIncomeResponse.data.incomeCount || 0;
          tempOrdersData[day - 1].orders = dailyOrdersResponse.data.orderCount || 0;
        }

        // Fetch list data
        const [pendingResponse, activeOrdersListResponse, topupsResponse] = await Promise.all([
          dataProvider.getListUnverifiedContractors(),
          dataProvider.getListActiveOrders(),
          dataProvider.getListRecentTopups({ perPage: 10 }),
        ]);

        setPendingItems(
          pendingResponse.data.map((item: any) => ({
            id: item.id || item.userId,
            name: item.firstName + ' ' + item.middleName + ' ' + item.lastName || item.username,
            avatar: item.thumbnailImageUrl || imagePaths.user,
            email: item.email,
            phoneNumber: item.phoneNumber,
            isContractorVerified: item.isContractorVerified,
          }))
        );

        setActiveOrders(
          activeOrdersListResponse.data.map((item: any) => ({
            id: item.id || item.orderId,
            name: item.postedByFullName || item.username,
            avatar: item.postedByThumbnail || imagePaths.user,
            description: item.description || item.brief,
            flowState: item.flowState,
            fulfilledById: item.fulfilledById,
            postedTimestamp: item.postedTimestamp,
          }))
        );

        setRecentTopups(
          topupsResponse.data.map((item: any) => ({
            id: item.id || item.transactionId,
            name: item.fullName || item.username,
            avatar: item.thumbnail || imagePaths.user,
            amount: item.amount || item.description,
            transactionType: item.transactionType,
            orderId: item.orderId,
            description: item.description || 'No description',
          }))
        );

        setIncomeData(tempIncomeData);
        setOrdersData(tempOrdersData);
        setLoading(false);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching data');
        setLoading(false);
      }
    };

    fetchData();
  }, [dataProvider]);

  return {
    stats,
    incomeData,
    ordersData,
    pendingItems,
    activeOrders,
    recentTopups,
    loading,
    error,
  };
};